import {useReportErrorContext} from '@github-ui/react-core/future/report-error-context'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav-events'
import {MarkGithubIcon} from '@primer/octicons-react'
import {Banner} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'

import styles from './AppInstallBannerPartial.module.css'
import {trackBannerEvent} from './banner-analytics'

// Chromium's non-standard event for offering an installable native app.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string}>
  prompt(): Promise<void>
}

// `navigator.getInstalledRelatedApps()` reports related native apps the browser
// knows are installed. Not part of the standard DOM lib typings.
interface NavigatorWithRelatedApps extends Navigator {
  getInstalledRelatedApps?: () => Promise<Array<{platform?: string; id?: string; url?: string}>>
}

export const DISMISSED_STORAGE_KEY = 'app-install-banner-dismissed-at'
// Re-offer the banner to active web users a month after they dismiss it, so a
// single dismissal doesn't hide the native app forever on that device.
const DISMISSAL_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

/** Captures Chromium's native Android install prompt and renders its banner. */
export function AppInstallBannerPartial() {
  const reportError = useReportErrorContext()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const impressionSentRef = useRef(false)
  // A soft navigation makes the server's path eligibility decision stale.
  // Ignore later prompts until a full page load remounts the partial.
  const navigatedAwayRef = useRef(false)

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Leave non-Play offers, such as desktop PWAs, to Chrome's install UI.
      const promptEvent = event as BeforeInstallPromptEvent
      if (!promptEvent.platforms?.includes('play')) return
      // Suppress Chrome's automatic mini-infobar first, even when we won't show
      // our own banner, so a prior dismissal isn't replaced by Chrome's prompt.
      event.preventDefault()
      // Ignore refires after navigation because server-side eligibility is stale.
      if (navigatedAwayRef.current) return
      // Recheck storage because dismissal can happen after mount.
      if (isDismissed()) return
      setDeferredPrompt(promptEvent)
      // Impression: the bar is now shown. Fire once per mount even if Chrome
      // refires the event on a later soft navigation.
      if (!impressionSentRef.current) {
        impressionSentRef.current = true
        trackBannerEvent('visible')
      }
    }
    const onAppInstalled = () => setDeferredPrompt(null)

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  useEffect(() => {
    // Best-effort Android telemetry; app detection never gates rendering.
    // `appinstalled` is PWA-focused and would misattribute native installs.
    const nav = navigator as NavigatorWithRelatedApps
    const detectInstalledApp = async () => {
      try {
        const apps = await nav.getInstalledRelatedApps?.()
        if (apps && apps.length > 0) {
          trackBannerEvent('app_detected_installed')
        }
      } catch {
        // Ignore detection failures; this is best-effort telemetry only.
      }
    }
    void detectInstalledApp()
  }, [])

  useEffect(() => {
    // Clear on navigation because only full renders evaluate server-side path eligibility.
    // This avoids duplicating the server's excluded-path list.
    const onSoftNav = () => {
      navigatedAwayRef.current = true
      setDeferredPrompt(null)
    }

    document.addEventListener(SOFT_NAV_STATE.SUCCESS, onSoftNav)
    window.addEventListener('popstate', onSoftNav)
    return () => {
      document.removeEventListener(SOFT_NAV_STATE.SUCCESS, onSoftNav)
      window.removeEventListener('popstate', onSoftNav)
    }
  }, [])

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return
    trackBannerEvent('get_app_click')
    // The saved event can only be prompted once, so clear it before prompting.
    const event = deferredPrompt
    setDeferredPrompt(null)
    try {
      await event.prompt()
      const {outcome} = await event.userChoice
      trackBannerEvent(outcome === 'accepted' ? 'prompt_accepted' : 'prompt_dismissed')
      // If the user declines Chrome's native prompt, start the cooldown so the
      // banner does not immediately reappear on the next `beforeinstallprompt`.
      if (outcome === 'dismissed') setDismissed()
    } catch (error) {
      // The event is single-use, so report the failure and leave the banner cleared.
      reportError(error instanceof Error ? error : new Error(String(error)))
    }
  }, [deferredPrompt, reportError])

  const handleDismiss = useCallback(() => {
    trackBannerEvent('dismiss')
    setDismissed()
    setDeferredPrompt(null)
  }, [])

  if (!deferredPrompt) return null

  return (
    <Banner
      actionsLayout="inline"
      aria-label="Install GitHub Mobile for Android"
      className={styles.Banner}
      description="GitHub Mobile for Android"
      flush
      hideTitle
      layout="compact"
      leadingVisual={<MarkGithubIcon />}
      onDismiss={handleDismiss}
      primaryAction={
        <Banner.PrimaryAction aria-label="Download GitHub Mobile for Android" onClick={handleInstall}>
          Download
        </Banner.PrimaryAction>
      }
      title="Install GitHub Mobile for Android"
      variant="info"
    />
  )
}

function isDismissed(): boolean {
  try {
    const dismissedAt = window.localStorage.getItem(DISMISSED_STORAGE_KEY)
    if (!dismissedAt) return false
    const elapsed = Date.now() - Number(dismissedAt)
    return Number.isFinite(elapsed) && elapsed >= 0 && elapsed < DISMISSAL_COOLDOWN_MS
  } catch {
    // Treat inaccessible storage (private mode, disabled cookies) as not dismissed.
    return false
  }
}

function setDismissed(): void {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()))
  } catch {
    // Ignore storage write failures; the banner simply won't persist dismissal.
  }
}
